import { memo } from 'react';
import { Star, Copy, CheckCheck, FileText } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { DealValueDisplay } from '@/components/pipeline/DealValueDisplay';
import { highlightMatches, formatNotesWithAttribution } from '../../utils';
import { PipelineCardContact } from './PipelineCardContact';
import { PipelineAppointmentSummary } from './PipelineAppointmentSummary';
import type { Customer, PresenceUser } from '../../types';

export interface PipelineCardCompactProps {
  customer: Customer;
  cardPresence: PresenceUser[];
  highlightTerms: string[];
  isCopied: Record<string, boolean>;
  copyToClipboard: (text: string | null | undefined, id: string) => void;
  onUpdateLeadScore: (customerId: number, score: number) => void;
}

/**
 * Compact (collapsed) view of a pipeline card
 * Shows name, lead score, deal value, notes preview, and contact info
 *
 * No longer reads from usePipelineContext() - all values threaded as props
 * so React.memo on PipelineCard can effectively prevent re-renders
 */
export const PipelineCardCompact = memo(function PipelineCardCompact({
  customer,
  cardPresence,
  highlightTerms,
  isCopied,
  copyToClipboard,
  onUpdateLeadScore,
}: PipelineCardCompactProps) {
  return (
    <div className="p-3 pl-4">
      {/* Lead quality indicator (colored border on left) - Based on Lead Score NOT age */}
      <div
        className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-xl ${
          (customer.leadScore || 0) >= 4
            ? 'bg-green-500'
            : (customer.leadScore || 0) >= 3
              ? 'bg-blue-500'
              : (customer.leadScore || 0) >= 2
                ? 'bg-amber-500'
                : (customer.leadScore || 0) >= 1
                  ? 'bg-orange-500'
                  : 'bg-slate-300'
        }`}
        title={`Lead Score: ${customer.leadScore || 0} stars (Color indicates quality, not age)`}
      />

      {/* Row 1: Customer Name + Deal Value (primary info) */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1 min-w-0 flex-1">
          <h3 className="card-customer-name truncate">
            {highlightMatches(customer.name ?? 'Unnamed deal', highlightTerms)}
          </h3>
          <button
            type="button"
            className="flex-shrink-0 focus:outline-none opacity-50 hover:opacity-100 transition-opacity"
            onClick={(e) => {
              e.stopPropagation();
              copyToClipboard(customer.name, `name-${customer.id}`);
            }}
            title="Copy name"
          >
            {isCopied[`name-${customer.id}`] ? (
              <CheckCheck className="h-3 w-3 text-green-500" />
            ) : (
              <Copy className="h-3 w-3 text-slate-400" />
            )}
          </button>
        </div>

        {/* Deal value - prominent */}
        <div className="flex-shrink-0">
          <DealValueDisplay
            customerId={customer.id}
            dealValue={customer.dealValue}
            additionalData={customer.additionalData}
            isCompact={true}
          />
        </div>
      </div>

      {/* Row 2: Lead Source Badge + Lead Score Stars */}
      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center gap-2">
          {/* Lead source badge */}
          {customer.source && (
            <span className="card-source-badge">
              {customer.source}
            </span>
          )}

          {/* Presence indicators (compact) */}
          {cardPresence.length > 0 && (
            <div className="flex -space-x-1">
              {cardPresence.slice(0, 2).map((person) => (
                <Tooltip key={`card-presence-${customer.id}-${person.userId}`}>
                  <TooltipTrigger asChild>
                    <div
                      className="h-5 w-5 rounded-full border-2 border-white dark:border-slate-800 text-[8px] font-semibold text-white flex items-center justify-center shadow-sm"
                      style={{ backgroundColor: person.color }}
                    >
                      {person.initials}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" align="center">
                    {person.name}
                  </TooltipContent>
                </Tooltip>
              ))}
              {cardPresence.length > 2 && (
                <div className="h-5 w-5 rounded-full bg-slate-200 dark:bg-slate-600 text-[8px] font-semibold flex items-center justify-center text-slate-600 dark:text-slate-200 border-2 border-white dark:border-slate-800">
                  +{cardPresence.length - 2}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Star rating - compact version */}
        <div className="flex items-center">
          {[1, 2, 3, 4, 5].map((score) => (
            <button
              key={score}
              type="button"
              className="focus:outline-none transition-transform hover:scale-110"
              onClick={(e) => {
                e.stopPropagation();
                onUpdateLeadScore(customer.id, score);
              }}
              title={`Rate ${score} star${score !== 1 ? 's' : ''}`}
            >
              <Star
                className={`h-3 w-3 ${
                  (customer.leadScore || 0) >= score
                    ? 'fill-yellow-400 text-yellow-400'
                    : 'text-slate-300 dark:text-slate-600'
                }`}
              />
            </button>
          ))}
        </div>
      </div>

      <PipelineAppointmentSummary customer={customer} />

      {/* Pipeline Notes Preview - More compact */}
      {customer.pipelineNotes && (
        <div className="mt-2 p-2 bg-blue-50/70 dark:bg-blue-950/20 rounded-lg">
          <div className="flex items-start gap-1.5">
            <FileText className="h-3 w-3 text-blue-500 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-blue-700 dark:text-blue-300 line-clamp-2 leading-relaxed">
              {formatNotesWithAttribution(
                customer.pipelineNotes.length > 150
                  ? `${customer.pipelineNotes.substring(0, 150)}…`
                  : customer.pipelineNotes
              )}
            </p>
          </div>
        </div>
      )}

      {/* Row 3: Contact Information - compact single line */}
      <PipelineCardContact
        customer={customer}
        highlightTerms={highlightTerms}
        isCopied={isCopied}
        copyToClipboard={copyToClipboard}
      />
    </div>
  );
});
